import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Mic, FolderOpen, Download } from "lucide-react";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { InnerBlock } from "@/components/InnerBlock";
import { InnerCTA } from "@/components/InnerCTA";
import { BLOG_POSTS } from "@/data/blog";
import { PODCAST_EPISODES } from "@/data/podcast";
import { DOWNLOADS } from "@/data/downloads";

const NAV_CARDS = [
  { icon: BookOpen, title: "Blog", desc: "Notes on PDPL, AI, buying software and running platforms in Oman.", to: "/resources/blog" },
  { icon: Mic, title: "Podcast", desc: "Conversations with operators, engineers and buyers.", to: "/resources/podcast" },
  { icon: FolderOpen, title: "Case studies", desc: "Six systems of ours running or launching. Written up properly.", to: "/work" },
  { icon: Download, title: "Downloads", desc: "Checklists and templates you can put to work.", to: "/resources/downloads" },
];

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  } catch (_) {
    return iso;
  }
}

export default function ResourcesIndex() {
  const latestPosts = BLOG_POSTS.slice(0, 3);
  const latestEpisode = PODCAST_EPISODES[0];

  return (
    <div data-testid="resources-index">
      <Seo title="Resources" description="Blog, podcast, case studies and downloads from Wafa Solutions." />
      <PageHero
        eyebrow="Resources"
        h1="Resources"
        intro="What we have learned building and running software in Oman. Written for people making decisions, not for search engines."
        breadcrumbs={[{ label: "Home", to: "/" }, { label: "Resources" }]}
      />

      {/* Four nav cards */}
      <InnerBlock tint="white">
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {NAV_CARDS.map((c) => {
            const Icon = c.icon;
            return (
              <Link
                key={c.to}
                to={c.to}
                data-testid={`resources-nav-${c.title.toLowerCase()}`}
                className="group flex flex-col rounded-lg border border-wafa-deep/10 bg-white p-6 transition-all duration-200 hover:-translate-y-1 hover:border-wafa-deep/25 hover:shadow-[0_18px_40px_-20px_rgba(14,79,58,0.25)]"
              >
                <Icon className="h-6 w-6 text-wafa-primary" />
                <div className="mt-4 font-heading text-lg font-semibold text-wafa-deep group-hover:text-wafa-primary">{c.title}</div>
                <p className="mt-2 text-sm text-wafa-ink/70">{c.desc}</p>
                <div className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-wafa-deep">
                  Go there <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </div>
              </Link>
            );
          })}
        </div>
      </InnerBlock>

      {/* Latest three blog posts */}
      <InnerBlock tint="cream" eyebrow="From the blog" heading="Latest posts">
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {latestPosts.map((p) => (
            <Link
              key={p.slug}
              to={`/resources/blog/${p.slug}`}
              data-testid={`resources-blog-teaser-${p.slug}`}
              className="group flex flex-col rounded-lg border border-wafa-deep/10 bg-white p-6 transition-all duration-200 hover:-translate-y-1 hover:border-wafa-deep/25 hover:shadow-[0_18px_40px_-20px_rgba(14,79,58,0.25)]"
            >
              <div className="text-[10px] font-semibold uppercase tracking-widest text-wafa-primary">{p.tag}</div>
              <div className="mt-3 font-heading text-lg font-semibold text-wafa-deep group-hover:text-wafa-primary">{p.title}</div>
              <p className="mt-3 text-sm text-wafa-ink/70 line-clamp-3">{p.dek}</p>
              <div className="mt-6 flex items-center justify-between text-xs text-wafa-ink/60">
                <span>{formatDate(p.date)}</span>
                <span>{p.readTime}</span>
              </div>
            </Link>
          ))}
        </div>
        <div className="mt-8">
          <Link to="/resources/blog" data-testid="resources-blog-all" className="inline-flex items-center gap-2 text-sm font-semibold text-wafa-deep hover:text-wafa-primary">
            All posts <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </InnerBlock>

      {/* Latest podcast episode */}
      <InnerBlock tint="white" eyebrow="Podcast" heading="Latest episode">
        <div className="rounded-lg border border-wafa-deep/10 bg-white p-6 lg:p-8">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="text-xs font-semibold uppercase tracking-widest text-wafa-primary">Episode {latestEpisode.number} · {latestEpisode.status}</div>
              <div className="mt-3 font-heading text-xl font-semibold text-wafa-deep">{latestEpisode.title}</div>
              <p className="mt-3 max-w-2xl text-sm text-wafa-ink/70">{latestEpisode.summary}</p>
            </div>
            <Link to="/resources/podcast" data-testid="resources-podcast-all" className="inline-flex items-center gap-2 text-sm font-semibold text-wafa-deep hover:text-wafa-primary">
              All episodes <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </InnerBlock>

      {/* Downloads teaser — the three guides */}
      <InnerBlock tint="cream" eyebrow="Downloads" heading="Guides you can put to work">
        <div className="grid gap-5 md:grid-cols-3">
          {DOWNLOADS.map((d) => (
            <Link
              key={d.slug}
              to={`/resources/downloads#${d.slug}`}
              data-testid={`resources-download-teaser-${d.slug}`}
              className="group flex flex-col rounded-lg border border-wafa-deep/10 bg-white p-6 transition-all duration-200 hover:-translate-y-1 hover:border-wafa-deep/25"
            >
              <Download className="h-5 w-5 text-wafa-primary" />
              <div className="mt-4 font-heading text-base font-semibold text-wafa-deep group-hover:text-wafa-primary">{d.title}</div>
              <p className="mt-2 text-sm text-wafa-ink/70">{d.subtitle}</p>
              <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-wafa-deep">
                Get it <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </div>
            </Link>
          ))}
        </div>
      </InnerBlock>

      <InnerCTA heading="Want us to write about something specific?" body="Tell us what you would like to see covered. If the answer is genuinely useful, we will write it." />
    </div>
  );
}
